import React, { useRef, useState } from 'react';
import { Send, ImagePlus, X, Loader2 } from 'lucide-react';

interface QuestionInputProps {
  questionInput: string;
  onInputChange: (value: string) => void;
  onSubmit: () => void;
  onImageUpload?: (file: File) => void;
  isImageUploading?: boolean;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

export const QuestionInput: React.FC<QuestionInputProps> = ({
  questionInput,
  onInputChange,
  onSubmit,
  onImageUpload,
  isImageUploading = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (selectedImage) {
        handleImageSubmit();
      } else if (questionInput.trim()) {
        onSubmit();
      }
    }
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setImageError(null);

    if (!file) return;

    // Validate file type
    if (!ALLOWED_TYPES.includes(file.type)) {
      setImageError('Please upload a JPG, PNG, GIF, or WebP image.');
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setImageError('Image must be less than 5MB.');
      return;
    }

    setSelectedImage(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleClearImage = () => {
    setSelectedImage(null);
    setPreviewUrl(null);
    setImageError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImageSubmit = () => {
    if (selectedImage && onImageUpload) {
      onImageUpload(selectedImage);
      handleClearImage();
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleSubmitClick = () => {
    if (selectedImage) {
      handleImageSubmit();
    } else {
      onSubmit();
    }
  };

  const canSubmit = selectedImage || questionInput.trim();

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur border-t border-gray-200 shadow-lg pb-[env(safe-area-inset-bottom)]">
      <div className="max-w-7xl mx-auto px-2 sm:px-4">
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp"
          onChange={handleImageSelect}
          className="hidden"
          aria-label="Upload image"
        />

        {/* Image Error */}
        {imageError && (
          <div className="pt-2">
            <div className="text-xs text-red-600 bg-red-50 px-3 py-1.5 rounded-lg inline-block">
              {imageError}
            </div>
          </div>
        )}

        <div className="py-2">
          {/* Input Field with Image Upload Inside */}
          <div className="bg-blue-50 border border-blue-100 rounded-2xl px-3 sm:px-4 py-2 flex items-center gap-2">
            {/* Image Upload Icon */}
            {onImageUpload && !previewUrl && (
              <button
                onClick={handleUploadClick}
                disabled={isImageUploading}
                className="text-gray-400 hover:text-blue-600 transition-colors flex-shrink-0 disabled:opacity-50"
                title="Upload image"
                aria-label="Upload image"
              >
                <ImagePlus className="w-5 h-5" />
              </button>
            )}

            {/* Image Preview (inline) */}
            {previewUrl && (
              <div className="relative flex-shrink-0">
                <img
                  src={previewUrl}
                  alt="Selected"
                  className="h-10 w-10 rounded-lg object-cover border border-blue-200"
                />
                <button
                  onClick={handleClearImage}
                  disabled={isImageUploading}
                  className="absolute -top-1.5 -right-1.5 p-0.5 bg-gray-600 text-white rounded-full hover:bg-gray-700 transition-colors disabled:opacity-50"
                  aria-label="Remove image"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}

            {/* Text Input */}
            <input
              value={questionInput}
              onChange={(e) => onInputChange(e.target.value)}
              onKeyDown={handleKeyDown}
              className="flex-1 min-w-0 bg-transparent outline-none text-sm text-gray-900 placeholder:text-gray-500"
              placeholder={previewUrl ? "Add a note (optional)..." : "Ask your math question..."}
              aria-label="Ask a new question"
              disabled={isImageUploading}
            />

            {/* Submit Button */}
            <button
              onClick={handleSubmitClick}
              disabled={!canSubmit || isImageUploading}
              className={`p-1.5 rounded-full transition-colors flex-shrink-0 ${
                canSubmit && !isImageUploading
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
              aria-label={selectedImage ? "Solve from image" : "Submit question"}
            >
              {isImageUploading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>

        {/* AI Disclaimer */}
        <div className="text-[10px] text-gray-400 text-center pb-1">
          mAIthzen can make mistakes. Please double check.
        </div>
      </div>
    </div>
  );
};

export default QuestionInput;
